import { html } from 'lit-html';
import { capitalCase } from 'change-case';
import { KatapultDialog } from '../katapult-elements/katapult-dialog';
import { Parallel } from '../../modules/Parallel';
import { isFlagEnabled } from '../../modules/FeatureFlags';

/**
 * Fetches the ids of any models that are synced to the current model's portal config.
 * @param {string} sourceModelId - The id of the model from which the portal config is being synced.
 * @returns {Promise<string[]>}
 */
export async function fetchSyncedPortalConfigs(sourceModelId) {
  const currentModelRef = globalThis.FirebaseWorker.ref(`photoheight/company_space/${sourceModelId}`);
  const syncToModels = (await currentModelRef.child('portal_config/sync_to_models').once('value')).val();
  if (!Array.isArray(syncToModels)) {
    if (syncToModels != null) console.warn('Expected sync_to_models to be an array, but got:', syncToModels);
    return [];
  }
  const sanitizedSyncToModels = syncToModels.filter((x) => typeof x === 'string');
  return sanitizedSyncToModels;
}

/**
 * Fetches the id and name of any models that are synced to the current model's portal config.
 * @param {string} sourceModelId - The id of the model from which the portal config is being synced.
 * @returns {Promise<{id: string, name: string}[]>}
 */
export async function fetchSyncedPortalConfigDetails(sourceModelId) {
  const syncedModelIds = await fetchSyncedPortalConfigs(sourceModelId);
  if (!syncedModelIds.length) return [];

  const currentModelRef = globalThis.FirebaseWorker.ref(`photoheight/company_space/${sourceModelId}`);
  const currentModelOwner = (await currentModelRef.child('model_owner').once('value')).val();
  const ownerModelOptionsRef = globalThis.FirebaseWorker.ref(`photoheight/company_space/${currentModelOwner}/model_options`);
  const ownerModelOptions = await ownerModelOptionsRef.once('value').then((s) => s.val());
  const syncToModelDetails = syncedModelIds.map((modelId) => {
    const modelOption = ownerModelOptions?.find((modelOption) => modelOption.value === modelId);
    const modelName = modelOption?.label ?? capitalCase(modelId);
    return { id: modelId, name: modelName };
  });

  return syncToModelDetails;
}

/**
 * Syncs the portal config of the current model to all models that are synced to it.
 * @param {string} sourceModelId - The id of the model from which the portal config is being synced.
 * @returns {Promise<void>}
 */
export async function syncPortalConfigs(sourceModelId) {
  const syncedModelIds = await fetchSyncedPortalConfigs(sourceModelId);
  const currentModelPortalConfigRef = globalThis.FirebaseWorker.ref(`photoheight/company_space/${sourceModelId}/portal_config`);
  const portalConfig = (await currentModelPortalConfigRef.once('value')).val();
  ['sync_to_models', 'app_draft_number_counter', 'app_number_counter'].forEach((key) => {
    delete portalConfig[key];
  });
  await Parallel.forEach(
    syncedModelIds,
    async (destinationModelId) => {
      const destinationModelPortalConfigRef = globalThis.FirebaseWorker.ref(
        `photoheight/company_space/${destinationModelId}/portal_config`
      );
      const destinationModelPortalConfig = structuredClone(portalConfig);
      destinationModelPortalConfig.synced_from = sourceModelId;
      await destinationModelPortalConfigRef.set(destinationModelPortalConfig);
    },
    2
  );
}

/**
 * Prompts the user to confirm that they want to sync the portal config to other models.
 * @param {string} sourceModelId - The id of the model from which the portal config is being synced.
 * @return {Promise<boolean>} - Returns true if the save process should be cancelled, false otherwise.
 */
export async function confirmSyncPortalConfig(sourceModelId) {
  const { currentUser } = globalThis.firebase.auth();
  const { claims } = await currentUser.getIdTokenResult();
  const shouldSyncPortalConfig = await isFlagEnabled('shared_workflow_configs', globalThis.firebase.database(), {
    companyId: claims.userGroup,
    userId: currentUser.uid
  });
  if (!shouldSyncPortalConfig) return false;

  const syncToModelDetails = await fetchSyncedPortalConfigDetails(sourceModelId);
  const hasModelsToSync = syncToModelDetails.length > 0;
  if (!hasModelsToSync) return false;

  const dialog = await KatapultDialog.confirm({
    html: html`
      <div>These changes will also be applied to the following models:</div>
      <ul>
        ${syncToModelDetails.map((details) => html` <li>${details.name}</li> `)}
      </ul>
    `
  });
  const confirmed = (await dialog.confirmed) === true;
  const cancelSaveProcess = !confirmed;
  return cancelSaveProcess;
}
